import React from 'react'
import { Box, List, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Dialog as MobileDialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { Dialog as DesktopDialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { formatDuration } from '@e3dc-react/shell/Datetime'
import { EdisonConnectPricing } from './EdisonConnectPricing'
import { AuthenticationInfo } from './AuthenticationInfo'
import { ChargingSessionModel } from '../models/charging-sessions.model'
import { useLocalizeNumberValue } from '@e3dc-react/shell/Hooks/useLocalizeNumberValue'
import { ChargingSessionStatusChip } from './ChargingSessionStatusChip'
import { MidMeterIcons } from './MidMeterIcons'
import { getCurrencyValue } from '../../shared/utils/currency-value'
import { TariffCategoryCell } from './TariffCategoryCell'
import { ChargePriceCell } from './ChargePriceCell'
import { format } from '@e3dc-react/shell/Libs/date'
import { DISCHARGE_CAPABLE_WALLBOX_TYPES } from 'src/Libs/wallboxes/consts'

const useStyles = makeStyles()(theme => ({
  listItem: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 0,
  },
  listItemTitle: {
    overflowWrap: 'anywhere',
    hyphens: 'auto',
    width: '33%',
  },
  listItemValue: {
    width: '67%',
    paddingLeft: theme.spacing(1),
    overflowWrap: 'anywhere',
    hyphens: 'auto',
  },
  dialogTitle: {
    marginRight: theme.spacing(7),
    wordBreak: 'break-all',
  },
}))

export interface EMobilityChargingSessionDetailModalProps {
  open: boolean
  onClose: () => void
  place: number
  session?: ChargingSessionModel
  shouldDisplayMidMeter: boolean
}

export const EMobilityChargingSessionDetailsModal: React.VFC<EMobilityChargingSessionDetailModalProps> = ({
  open,
  place,
  onClose,
  session,
  shouldDisplayMidMeter,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const getLocalizedNumber = useLocalizeNumberValue()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!session) {
    onClose()
    return null
  }

  /* We are displaying discharge price only if wallbox type is BiDi - EdisonConnect */
  const isDischargeCapable = DISCHARGE_CAPABLE_WALLBOX_TYPES.includes(session.wallbox.type)

  const Dialog = isMobile ? MobileDialog : DesktopDialog

  return (
    <Dialog
      open
      title={t('emobility.chargingSession.chargingSession', { sessionID: session.emobilitySessionId })}
      onClose={onClose}
      place={place}
      fullScreen={isMobile}
      dialogTitleClassName={classes.dialogTitle}
      maxWidth="sm"
      divider={false}
    >
      <List>
        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.wallbox')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {session.wallbox.name
              ? t('emobility.chargingSession.wallboxNameAndId', { wallboxName: session.wallbox.name, wallboxId: session.wallbox.id })
              : t('emobility.chargingSession.wallboxId', { id: session.wallbox.id })}
          </Typography>
        </ListItem>

        {shouldDisplayMidMeter && (
          <ListItem divider className={classes.listItem} disableGutters>
            <Typography variant="subtitle1" className={classes.listItemTitle}>
              {t('emobility.chargingSession.mid-meter-info')}
            </Typography>

            <Box className={classes.listItemValue}>
              <MidMeterIcons
                midMeterConnected={session.midInfo.midMeterConnected}
                midEnergyInconsistent={session.midInfo.midEnergyInconsistent}
                midSessionOffline={session.midInfo.midSessionOffline}
              />
            </Box>
          </ListItem>
        )}

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.status')}
          </Typography>

          {/* At the moment, data migration frequently introduces "null" statuses, so we temporarily guard against them */}
          {session.status ? (
            <Box className={classes.listItemValue}>
              <ChargingSessionStatusChip status={session.status} />
            </Box>
          ) : (
            <Typography variant="body2" className={classes.listItemValue}>
              {'-'}
            </Typography>
          )}
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.timeRange')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {`${format(new Date(session.startDateTime), t('common.format.date-fns-date-time'))} - ${
              session.endDateTime ? format(new Date(session.endDateTime), t('common.format.date-fns-date-time')) : 'N/A'
            }`}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.created')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {format(new Date(session.startDateTime), t('common.format.date-fns-date-time'))}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.updated')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {session.lastUpdated ? format(new Date(session.lastUpdated), t('common.format.date-fns-date-time')) : 'N/A'}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.authentication')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            <AuthenticationInfo
              authentication={session.authentication}
              rfid={session.authentication.rfid}
              cardName={session.authentication.cardName}
            />
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.tariffs.tariff-category')}
          </Typography>

          <TariffCategoryCell tariffCategory={session.tariffCategory} />
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.charged-energy')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {`${t('emobility.chargingSession.chargingGrid')} ${t('common.format.kwh', {
              value: getLocalizedNumber(session.gridEnergyKWh),
            })} • ${t('emobility.chargingSession.chargingSolar')} ${t('common.format.kwh', {
              value: getLocalizedNumber(session.energySolarKWh),
            })} • ${t('emobility.chargingSession.chargingAll')} ${t('common.format.kwh', {
              value: getLocalizedNumber(session.consumptionKWh),
            })}`}
          </Typography>
        </ListItem>

        <ListItem divider={session.isCostFinal || isDischargeCapable} className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.chargeDuration')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {`${t('emobility.chargingSession.active')} ${formatDuration(session.chargeTimeActiveMilliseconds)} • ${t(
              'emobility.chargingSession.inactive'
            )} ${formatDuration(session.chargeTimeInactiveMilliseconds)}`}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.charge-price-per-kwh')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            <ChargePriceCell chargePrice={session.chargePrice} currencyCode={session.currencyCode} />
          </Typography>
        </ListItem>

        {isDischargeCapable && <EdisonConnectPricing session={session} />}

        <ListItem className={classes.listItem} disableGutters divider>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.total-cost')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {t('emobility.chargingSession.value-including-vat', { value: getCurrencyValue(session.totalCost ?? 0, session.currencyCode) })}
          </Typography>
        </ListItem>
        <ListItem className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.ocpi-token-id')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {session.sessionId}
          </Typography>
        </ListItem>
      </List>
    </Dialog>
  )
}
